<template>
  <div class="create-order-container">
    <div class="page-header">
      <h1>{{ $t('PurchaseOrder.Create.line4_text') }}</h1>
      <p class="subtitle">{{ $t('PurchaseOrder.Create.line5_text') }}</p>
    </div>
    <div class="form-container">
      <!-- 第一步：选择商家 -->
      <div class="form-section">
        <div class="section-title">
          <span class="step-number">1</span>
          <h3>{{ $t('PurchaseOrder.Create.line12_text') }}</h3>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('PurchaseOrder.Create.line15_text') }} <span class="required">*</span></label>
          <select v-model="formData.sellerId" class="form-control" @change="onSellerChange">
            <option value="">{{ $t('PurchaseOrder.Create.line17_text') }}</option>
            <option v-for="seller in sellers" :key="seller.id" :value="seller.id">
              {{ seller.customerName }}
            </option>
          </select>
          <span v-if="errors.sellerId" class="error-message">{{ errors.sellerId }}</span>
        </div>
        <!-- 商家信息展示 -->
        <div v-if="currentSeller" class="seller-info-card">
          <div class="info-row">
            <span class="label">{{ $t('PurchaseOrder.Create.line27_text') }}</span>
            <span class="value">{{ currentSeller.customerName }}</span>
          </div>
          <div class="info-row">
            <span class="label">{{ $t('PurchaseOrder.Create.line31_text') }}</span>
            <span class="value">{{ currentSeller.contactName }}</span>
          </div>
          <div class="info-row">
            <span class="label">{{ $t('PurchaseOrder.Create.line35_text') }}</span>
            <span class="value">{{ currentSeller.contactPhone }}</span>
          </div>
          <div class="info-row">
            <span class="label">{{ $t('PurchaseOrder.Create.line39_text') }}</span>
            <span class="value">¥{{ currentSeller.totalPurchase }}</span>
          </div>
        </div>
      </div>
      <!-- 第二步：选择商品 -->
      <div class="form-section" v-if="formData.sellerId">
        <div class="section-title">
          <span class="step-number">2</span>
          <h3>{{ $t('PurchaseOrder.Create.line48_text') }}</h3>
        </div>
        <!-- 搜索和筛选 -->
        <div class="search-bar">
          <input v-model="productSearch" type="text" :placeholder="$t('PurchaseOrder.Create.line52_placeholder')" class="form-control" @input="searchProducts" />
          <button class="btn btn-secondary" @click="loadProducts">
            <i class="icon-search"></i> 搜索
          </button>
        </div>
        <!-- 商品列表 -->
        <div class="products-grid">
          <div v-for="product in availableProducts" :key="product.id" class="product-card">
            <img :src="getImageUrl(product.image)" :alt="product.name" class="product-image">
            <div class="product-card-body">
              <h4 class="product-name">{{ product.name }}</h4>
              <p class="product-code">{{ product.code }}</p>
              <div class="product-price">¥{{ product.price }}</div>
              <div class="product-actions">
                <input v-model.number="product.selectedQty" type="number" min="0" class="qty-input" :placeholder="$t('PurchaseOrder.Create.line66_placeholder')" />
                <button class="btn btn-primary btn-sm" @click="addProductToOrder(product)" :disabled="product.selectedQty <= 0">
                  添加
                </button>
              </div>
            </div>
          </div>
        </div>
        <div v-if="availableProducts.length === 0" class="empty-state">
          <p>{{ $t('PurchaseOrder.Create.line75_text') }}</p>
        </div>
      </div>
      <!-- 第三步：订单详情 -->
      <div class="form-section" v-if="orderItems.length > 0">
        <div class="section-title">
          <span class="step-number">3</span>
          <h3>{{ $t('PurchaseOrder.Create.line82_text') }}</h3>
        </div>
        <table class="order-items-table">
          <thead>
            <tr>
              <th>{{ $t('common.products') }}</th>
              <th style="width: 80px;">{{ $t('PurchaseOrder.Create.line88_text') }}</th>
              <th style="width: 100px;">{{ $t('PurchaseOrder.Create.line89_text') }}</th>
              <th style="width: 80px;">{{ $t('PurchaseOrder.Create.line90_text') }}</th>
              <th style="width: 50px;">{{ $t('PurchaseOrder.Create.line91_text') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in orderItems" :key="item.productId">
              <td>
                <div class="item-name">
                  <span>{{ item.productName }}</span>
                </div>
              </td>
              <td>¥{{ item.unitPrice }}</td>
              <td>
                <input v-model.number="item.quantity" type="number" min="1" class="qty-input" @change="updateOrderItem(item)" />
              </td>
              <td>¥{{ (item.unitPrice * item.quantity).toFixed(2) }}</td>
              <td>
                <button class="btn-delete" @click="removeOrderItem(item.productId)">
                  <i class="icon-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <!-- 订单统计 -->
        <div class="order-summary">
          <div class="summary-row">
            <span>{{ $t('PurchaseOrder.Create.line117_text') }}</span>
            <span class="amount">¥{{ productTotal }}</span>
          </div>
          <div class="summary-row">
            <span>{{ $t('PurchaseOrder.Create.line121_text') }}</span>
            <div class="form-group">
              <input v-model.number="formData.discountAmount" type="number" min="0" step="0.01" class="form-control" placeholder="0.00" style="width: 120px;" />
            </div>
          </div>
          <div class="summary-row total">
            <span>{{ $t('PurchaseOrder.Create.line127_text') }}</span>
            <span class="amount">¥{{ orderTotal }}</span>
          </div>
        </div>
      </div>
      <!-- 第四步：配送和备注 -->
      <div class="form-section">
        <div class="section-title">
          <span class="step-number">4</span>
          <h3>{{ $t('PurchaseOrder.Create.line136_text') }}</h3>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label class="form-label">{{ $t('PurchaseOrder.Create.line140_text') }} <span class="required">*</span></label>
            <input v-model="formData.receiverName" type="text" :placeholder="$t('PurchaseOrder.Create.line141_placeholder')" class="form-control" />
            <span v-if="errors.receiverName" class="error-message">{{ errors.receiverName }}</span>
          </div>
          <div class="form-group">
            <label class="form-label">{{ $t('PurchaseOrder.Create.line145_text') }} <span class="required">*</span></label>
            <input v-model="formData.receiverPhone" type="tel" :placeholder="$t('PurchaseOrder.Create.line146_placeholder')" class="form-control" />
            <span v-if="errors.receiverPhone" class="error-message">{{ errors.receiverPhone }}</span>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('PurchaseOrder.Create.line151_text') }} <span class="required">*</span></label>
          <input v-model="formData.deliveryAddress" type="text" :placeholder="$t('PurchaseOrder.Create.line152_placeholder')" class="form-control" />
          <span v-if="errors.deliveryAddress" class="error-message">{{ errors.deliveryAddress }}</span>
        </div>
        <div class="form-group">
          <label class="form-label">{{ $t('PurchaseOrder.Create.line156_text') }}</label>
          <textarea v-model="formData.remarks" :placeholder="$t('PurchaseOrder.Create.line157_placeholder')" class="form-control" rows="4"></textarea>
        </div>
      </div>
      <!-- 提交按钮 -->
      <div class="form-actions">
        <button class="btn btn-secondary" @click="goBack">
          <i class="icon-chevron-left"></i> 返回
        </button>
        <button class="btn btn-primary" @click="submitOrder" :disabled="submitting || orderItems.length === 0" v-if="formData.sellerId">
          <i v-if="!submitting" class="icon-check"></i>
          <span v-if="submitting">{{ $t('PurchaseOrder.Create.line167_text') }}</span>
          <span v-else>{{ $t('PurchaseOrder.Create.line168_text') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { getImageUrl } from '@/utils/image';

export default {
  name: 'CreatePurchaseOrder',
  data() {
    return {
      formData: {
        sellerId: '',
        items: [],
        discountAmount: 0,
        receiverName: '',
        receiverPhone: '',
        deliveryAddress: '',
        remarks: ''
      },
      orderItems: [],
      sellers: [],
      availableProducts: [],
      productSearch: '',
      submitting: false,
      errors: {},
      currentSeller: null
    };
  },
  computed: {
    productTotal() {
      return this.orderItems
        .reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
        .toFixed(2);
    },
    orderTotal() {
      return (parseFloat(this.productTotal) - parseFloat(this.formData.discountAmount || 0))
        .toFixed(2);
    }
  },
  methods: {
    getImageUrl,
    ...mapActions('purchaseOrder', [
      'getSellers',
      'getProductsBySeller',
      'createOrder'
    ]),
    async loadSellers() {
      try {
        this.sellers = await this.getSellers();
      } catch (error) {
        this.$message.error('加载商家列表失败');
      }
    },
    async onSellerChange() {
      if (!this.formData.sellerId) {
        this.availableProducts = [];
        this.currentSeller = null;
        return;
      }
      this.currentSeller = this.sellers.find(s => s.id === this.formData.sellerId);
      await this.loadProducts();
    },
    async loadProducts() {
      try {
        const response = await this.getProductsBySeller({
          sellerId: this.formData.sellerId,
          search: this.productSearch
        });
        this.availableProducts = response.map(product => ({
          ...product,
          selectedQty: 0
        }));
      } catch (error) {
        this.$message.error('加载产品列表失败');
      }
    },
    searchProducts() {
      // 搜索会在加载时触发
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadProducts();
      }, 500);
    },
    addProductToOrder(product) {
      if (product.selectedQty <= 0) {
        this.$message.warning('请输入有效的数量');
        return;
      }
      const existingItem = this.orderItems.find(item => item.productId === product.id);
      if (existingItem) {
        existingItem.quantity += product.selectedQty;
      } else {
        this.orderItems.push({
          productId: product.id,
          productName: product.name,
          productCode: product.code,
          unitPrice: product.price,
          quantity: product.selectedQty
        });
      }
      product.selectedQty = 0;
      this.$message.success('已添加到订单');
    },
    updateOrderItem(item) {
      if (item.quantity <= 0) {
        this.removeOrderItem(item.productId);
      }
    },
    removeOrderItem(productId) {
      const index = this.orderItems.findIndex(item => item.productId === productId);
      if (index > -1) {
        this.orderItems.splice(index, 1);
      }
    },
    validateForm() {
      this.errors = {};
      if (!this.formData.sellerId) {
        this.errors.sellerId = '请选择商家';
      }
      if (this.orderItems.length === 0) {
        this.$message.error('请添加至少一件商品');
        return false;
      }
      if (!this.formData.receiverName) {
        this.errors.receiverName = '请输入收货人';
      }
      if (!this.formData.receiverPhone) {
        this.errors.receiverPhone = '请输入收货电话';
      }
      if (!this.formData.deliveryAddress) {
        this.errors.deliveryAddress = '请输入送货地址';
      }
      return Object.keys(this.errors).length === 0;
    },
    async submitOrder() {
      if (!this.validateForm()) {
        return;
      }
      this.submitting = true;
      try {
        const orderData = {
          ...this.formData,
          items: this.orderItems,
          totalAmount: parseFloat(this.orderTotal)
        };
        const response = await this.createOrder(orderData);
        this.$message.success('采购单已创建');
        this.$router.push(`/purchase-order/${response.id}`);
      } catch (error) {
        this.$message.error(error.message || '创建采购单失败');
      } finally {
        this.submitting = false;
      }
    },
    goBack() {
      this.$router.back();
    }
  },
  mounted() {
    this.loadSellers();
    // 如果从购物车创建，获取商家ID
    const sellerId = this.$route.query.sellerId;
    if (sellerId) {
      this.formData.sellerId = sellerId;
      this.onSellerChange();
    }
  }
};
</script>

<style scoped lang="scss">
.create-order-container {
  padding: 20px;
  
  .page-header {
    margin-bottom: 30px;
    h1 {
      font-size: 28px;
      font-weight: bold;
      color: #2c3e50;
      margin-bottom: 8px;
    }
    .subtitle {
      color: #999;
      font-size: 14px;
      margin: 0;
    }
  }
  
  .form-container {
    max-width: 1000px;
    margin: 0 auto;
    
    .form-section {
      background: white;
      border-radius: 8px;
      padding: 24px;
      margin-bottom: 20px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      
      .section-title {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
        padding-bottom: 16px;
        border-bottom: 2px solid #eee;
        
        .step-number {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background-color: #3498db;
          color: white;
          border-radius: 50%;
          font-weight: bold;
          font-size: 14px;
        }
        
        h3 {
          margin: 0;
          font-size: 16px;
          color: #2c3e50;
        }
      }
      
      .form-group {
        margin-bottom: 16px;
        &:last-child {
          margin-bottom: 0;
        }
        
        .form-label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #666;
          margin-bottom: 8px;
          
          .required {
            color: #e74c3c;
          }
        }
        
        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 13px;
          font-family: inherit;
          transition: all 0.3s ease;
          
          &:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.1);
          }
          
          &:disabled {
            background-color: #f5f7fa;
            cursor: not-allowed;
          }
        }
        
        textarea.form-control {
          resize: vertical;
          font-family: inherit;
        }
        
        .error-message {
          display: block;
          color: #e74c3c;
          font-size: 12px;
          margin-top: 4px;
        }
      }
      
      .form-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 16px;
      }
      
      .seller-info-card {
        background-color: #f5f7fa;
        border: 1px solid #eee;
        border-radius: 6px;
        padding: 16px;
        margin-top: 16px;
        
        .info-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 13px;
          border-bottom: 1px solid #eee;
          &:last-child {
            border-bottom: none;
          }
          
          .label {
            color: #666;
            font-weight: 500;
          }
          
          .value {
            color: #2c3e50;
          }
        }
      }
      
      .search-bar {
        display: flex;
        gap: 12px;
        margin-bottom: 20px;
        
        input {
          flex: 1;
        }
        
        button {
          white-space: nowrap;
        }
      }
      
      .products-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 16px;
        margin-top: 20px;
        
        .product-card {
          border: 1px solid #eee;
          border-radius: 8px;
          overflow: hidden;
          transition: all 0.3s ease;
          
          &:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            transform: translateY(-2px);
          }
          
          .product-image {
            width: 100%;
            height: 160px;
            object-fit: cover;
            background-color: #f5f7fa;
          }
          
          .product-card-body {
            padding: 12px;
            
            .product-name {
              margin: 0;
              font-size: 13px;
              font-weight: 500;
              color: #2c3e50;
              margin-bottom: 4px;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            }
            
            .product-code {
              margin: 0;
              font-size: 11px;
              color: #999;
              margin-bottom: 8px;
            }
            
            .product-price {
              font-size: 16px;
              color: #3498db;
              font-weight: bold;
              margin-bottom: 12px;
            }
            
            .product-actions {
              display: flex;
              gap: 8px;
              
              .qty-input {
                flex: 1;
                padding: 6px 8px;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 12px;
                text-align: center;
                
                &:focus {
                  outline: none;
                  border-color: #3498db;
                }
              }
              
              button {
                flex: 1;
              }
            }
          }
        }
      }
      
      .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #999;
        font-size: 13px;
      }
      
      .order-items-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 20px;
        
        thead {
          tr {
            background-color: #f5f7fa;
            border-bottom: 1px solid #eee;
            
            th {
              padding: 12px 16px;
              text-align: left;
              font-size: 12px;
              font-weight: 600;
              color: #666;
            }
          }
        }
        
        tbody {
          tr {
            border-bottom: 1px solid #eee;
            
            &:hover {
              background-color: #f9f9f9;
            }
            
            td {
              padding: 12px 16px;
              font-size: 13px;
              vertical-align: middle;
              
              .item-name {
                color: #2c3e50;
                font-weight: 500;
              }
              
              .qty-input {
                width: 80px;
                padding: 6px 8px;
                border: 1px solid #ddd;
                border-radius: 4px;
                text-align: center;
                
                &:focus {
                  outline: none;
                  border-color: #3498db;
                }
              }
              
              .btn-delete {
                background: none;
                border: none;
                color: #e74c3c;
                cursor: pointer;
                font-size: 16px;
                
                &:hover {
                  color: #c0392b;
                }
              }
            }
          }
        }
      }
      
      .order-summary {
        background-color: #f5f7fa;
        border: 1px solid #eee;
        border-radius: 6px;
        padding: 16px;
        
        .summary-row {
          display: flex;
          justify-content: flex-end;
          gap: 20px;
          padding: 8px 0;
          font-size: 13px;
          border-bottom: 1px solid #eee;
          
          &:last-child {
            border-bottom: none;
          }
          
          &.total {
            font-size: 16px;
            font-weight: bold;
            color: #2c3e50;
            padding-top: 12px;
            border-top: 1px solid #ddd;
            
            .amount {
              color: #3498db;
            }
          }
          
          .amount {
            color: #2c3e50;
            font-weight: 500;
            min-width: 100px;
            text-align: right;
          }
          
          .form-group {
            margin: 0;
            
            input {
              width: 120px;
            }
          }
        }
      }
    }
    
    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-top: 30px;
      
      button {
        min-width: 150px;
      }
    }
  }
}
</style>